import { useQuery } from '@tanstack/react-query';
import { CalendarIcon, ShieldIcon, AppWindowIcon } from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Skeleton } from '@/components/skeleton/skeleton';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { getRoleQueryOptions } from '@/features/roles/api/get-role.api';
import { getPermissionDescription } from '@/features/roles/utils/permission-description';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

export interface DetailRoleDialogProps extends BaseDialogProps {
  roleId: string;
}

export default function DetailRoleDialog({
  dialog,
  roleId,
}: DetailRoleDialogProps) {
  const { closeDetailRoleDialog } = useV2DialogContext();

  const getRoleQuery = useQuery(getRoleQueryOptions({ roleId }));
  const role = getRoleQuery.data?.result;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailRoleDialog();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Detail Role</DialogTitle>
          <DialogDescription>
            Informasi lengkap tentang role ini.
          </DialogDescription>
        </DialogHeader>

        {getRoleQuery.isPending ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        ) : getRoleQuery.isError || !role ? (
          <p className="text-sm text-destructive">
            Gagal memuat data role. Silakan coba lagi.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
              <InfoRow
                icon={<ShieldIcon className="size-4" />}
                label="Nama Role"
                value={role.role}
              />
              <InfoRow
                icon={<AppWindowIcon className="size-4" />}
                label="App"
                value={role.app}
              />
              <InfoRow
                icon={<CalendarIcon className="size-4" />}
                label="Dibuat Pada"
                value={formatDate(role.created_at)}
              />
              <InfoRow
                icon={<CalendarIcon className="size-4" />}
                label="Diperbarui Pada"
                value={formatDate(role.updated_at)}
              />
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-foreground">
                Permission ({role.permissions.length})
              </p>
              {role.permissions.length === 0 ? (
                <div className="rounded-md border border-border p-4 text-center text-sm text-muted-foreground">
                  Tidak ada permission yang ditetapkan
                </div>
              ) : (
                <div className="flex flex-col divide-y divide-border rounded-md border border-border">
                  {role.permissions.map(permission => (
                    <div
                      key={permission.id}
                      className="flex items-start gap-3 p-3"
                    >
                      <Badge variant="info" className="shrink-0">
                        {permission.name}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {getPermissionDescription(permission.name)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}
